import { makeAutoObservable, reaction, runInAction } from "mobx"
import { Howl } from "howler"
import { indexDBService } from "@/entities/indexdb"
import { addAudioBreadcrumb } from "@/entities/sentry"
import type {
	AudioMetadata,
	AudioPlaybackRate,
	AudioStartOptions,
} from "./types"

class AudioEngine {
	currentUrl: string | null = null
	title: string | null = null
	id: string | null = null
	totalDuration = 0
	currentPosition = 0
	playbackRate: AudioPlaybackRate = 1
	isPlaying = false
	isLoading = false
	isSeeking = false

	private howl: Howl | null = null
	/**
	 * Howler sound id of our single playback instance. Howler's play()
	 * without an id spawns a NEW simultaneous sound whenever it doesn't see
	 * exactly one paused sound — a double-resume race (e.g. two quick
	 * seekToAndPlay calls before the async isPlaying flip) produced two
	 * audible tracks. With the id, play(id) on an already-playing sound is
	 * a no-op.
	 */
	private soundId: number | null = null
	private rafId: number | null = null
	private onProgressCallback:
		| ((pos: number) => boolean | void)
		| null = null

	constructor() {
		makeAutoObservable<
			AudioEngine,
			"howl" | "soundId" | "rafId" | "onProgressCallback" | "handleBeforeUnload"
		>(this, {
			howl: false,
			soundId: false,
			rafId: false,
			onProgressCallback: false,
			handleBeforeUnload: false,
		}, { autoBind: true })

		reaction(
			() => this.isPlaying,
			(playing) => {
				if (playing) {
					window.addEventListener("beforeunload", this.handleBeforeUnload)
				} else {
					window.removeEventListener("beforeunload", this.handleBeforeUnload)
				}
			}
		)
	}

	private handleBeforeUnload = (e: BeforeUnloadEvent) => {
		e.preventDefault()
		this.persistPosition()
	}

	/** Load audio and set metadata without playing. Common player shows the item. */
	load(
		url: string,
		metadata: AudioMetadata,
		startPosition = 0
	) {
		this.persistPosition()
		this.destroyHowl()

		this.currentUrl = url
		this.title = metadata.title
		this.totalDuration = metadata.totalDuration
		this.id = metadata.id
		this.currentPosition = startPosition
		this.isPlaying = false
		this.isLoading = true
		this.isSeeking = false

		this.howl = new Howl({
			src: [url],
			rate: this.playbackRate,
			onload: () => {
				runInAction(() => {
					this.isLoading = false
				})
				if (startPosition > 0) {
					this.howl!.seek(startPosition)
				}
				addAudioBreadcrumb("Audio loaded", { id: metadata.id, title: metadata.title, startPosition })
				// Don't play — just loaded and ready
			},
			onplay: () => {
				runInAction(() => { this.isPlaying = true })
				addAudioBreadcrumb("Audio playing", { id: this.id, pos: this.currentPosition, rate: this.playbackRate })
				this.startRafLoop()
			},
			onpause: () => {
				runInAction(() => { this.isPlaying = false })
				addAudioBreadcrumb("Audio paused", { id: this.id, pos: this.currentPosition })
				this.stopRafLoop()
			},
			onstop: () => {
				runInAction(() => { this.isPlaying = false; this.currentPosition = 0 })
				addAudioBreadcrumb("Audio stopped", { id: this.id })
				this.stopRafLoop()
			},
			onend: () => {
				this.persistPosition()
				runInAction(() => { this.isPlaying = false; this.currentPosition = this.totalDuration })
				addAudioBreadcrumb("Audio finished/ended", { id: this.id })
				this.stopRafLoop()
			},
			onloaderror: (_id, error) => {
				console.error("Audio load error:", error)
				addAudioBreadcrumb("Audio load error", { id: this.id, error: String(error) })
				runInAction(() => { this.isLoading = false })
			},
			onplayerror: (_id, error) => {
				console.error("Audio play error:", error)
				addAudioBreadcrumb("Audio play error", { id: this.id, error: String(error) })
				runInAction(() => { this.isPlaying = false })
			},
		})
	}

	startPlay(
		url: string,
		metadata: AudioMetadata,
		options?: AudioStartOptions
	) {
		this.persistPosition()
		this.destroyHowl()

		const rate = options?.playbackRate ?? this.playbackRate
		this.onProgressCallback = options?.onProgress ?? null

		this.currentUrl = url
		this.title = metadata.title
		this.totalDuration = metadata.totalDuration
		this.id = metadata.id
		this.currentPosition = options?.startPosition ?? 0
		this.playbackRate = rate
		this.isPlaying = false
		this.isLoading = true
		this.isSeeking = false

		const startPosition = options?.startPosition ?? 0

		this.howl = new Howl({
			src: [url],
			rate,
			onload: () => {
				runInAction(() => {
					this.isLoading = false
				})
				if (startPosition > 0) {
					this.howl!.seek(startPosition)
				}
				addAudioBreadcrumb("Audio loaded (startPlay)", { id: metadata.id, title: metadata.title, startPosition })
				this.soundId = this.howl!.play()
			},
			onplay: () => {
				runInAction(() => {
					this.isPlaying = true
				})
				addAudioBreadcrumb("Audio playing", { id: this.id, pos: this.currentPosition, rate: this.playbackRate })
				this.startRafLoop()
			},
			onpause: () => {
				runInAction(() => {
					this.isPlaying = false
				})
				addAudioBreadcrumb("Audio paused", { id: this.id, pos: this.currentPosition })
				this.stopRafLoop()
			},
			onstop: () => {
				runInAction(() => {
					this.isPlaying = false
					this.currentPosition = 0
				})
				addAudioBreadcrumb("Audio stopped", { id: this.id })
				this.stopRafLoop()
			},
			onend: () => {
				this.persistPosition()
				runInAction(() => {
					this.isPlaying = false
					this.currentPosition = this.totalDuration
				})
				addAudioBreadcrumb("Audio finished/ended", { id: this.id })
				this.stopRafLoop()
			},
			onloaderror: (_id, error) => {
				console.error("Audio load error:", error)
				addAudioBreadcrumb("Audio load error", { id: this.id, error: String(error) })
				runInAction(() => {
					this.isLoading = false
				})
			},
			onplayerror: (_id, error) => {
				console.error("Audio play error:", error)
				addAudioBreadcrumb("Audio play error", { id: this.id, error: String(error) })
				runInAction(() => {
					this.isPlaying = false
				})
			},
		})
	}

	pause() {
		this.howl?.pause()
		this.persistPosition()
	}

	resume() {
		if (!this.howl) return
		if (this.soundId !== null) {
			// Resuming an already-playing sound id is a safe no-op in howler
			this.howl.play(this.soundId)
		} else {
			this.soundId = this.howl.play()
		}
	}

	stop() {
		this.persistPosition()
		this.howl?.stop()
		this.onProgressCallback = null
	}

	reset() {
		this.persistPosition()
		this.destroyHowl()
		this.currentUrl = null
		this.title = null
		this.id = null
		this.totalDuration = 0
		this.currentPosition = 0
		this.isPlaying = false
		this.isLoading = false
		this.isSeeking = false
	}

	seekTo(position: number) {
		if (!this.howl) return
		addAudioBreadcrumb("Audio seekTo", { id: this.id, targetPosition: position })
		if (this.soundId !== null) {
			this.howl.seek(position, this.soundId)
		} else {
			this.howl.seek(position)
		}
		this.currentPosition = position
	}

	setPlaybackRate(rate: AudioPlaybackRate) {
		this.playbackRate = rate
		if (this.howl) {
			this.howl.rate(rate)
		}
	}

	private startRafLoop() {
		this.stopRafLoop()
		const tick = () => {
			if (!this.howl || !this.howl.playing()) return
			const pos = this.howl.seek() as number
			runInAction(() => {
				this.currentPosition = pos
			})
			if (this.onProgressCallback) {
				const result = this.onProgressCallback(pos)
				if (result === false) {
					this.stop()
					return
				}
			}
			this.rafId = requestAnimationFrame(tick)
		}
		this.rafId = requestAnimationFrame(tick)
	}

	private stopRafLoop() {
		if (this.rafId !== null) {
			cancelAnimationFrame(this.rafId)
			this.rafId = null
		}
	}

	private persistPosition() {
		if (this.id && this.currentPosition > 0) {
			indexDBService.saveAudioPosition({
				id: this.id,
				position: this.currentPosition,
			})
		}
	}

	private destroyHowl() {
		this.stopRafLoop()
		if (this.howl) {
			this.howl.unload()
			this.howl = null
		}
		this.soundId = null
		this.onProgressCallback = null
	}
}

export const audioEngine = new AudioEngine()
